import { Injectable } from '@angular/core';
import { BullhornEntityTypes, BullhornNamespace } from 'datasource-service-lib';
import { EntityControllerService } from 'entity-picker-lib';
import { IDataSourceApiService, IEntity, IEntityProfile } from 'interfaces-lib';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { IEntityProfileService } from '../../app-entity-preview/ientityprofileservice';
import { ServiceProviderService } from '../service.provider.service';

@Injectable({ providedIn: 'root' })
export class BullhornEntityProfileService implements IEntityProfileService {
  private apiService: IDataSourceApiService;

  private entityProfileSelectedSubject = new ReplaySubject<IEntityProfile>();
  entityProfileSelected$ = this.entityProfileSelectedSubject.asObservable();

  constructor(private entityControllerService: EntityControllerService, sps: ServiceProviderService) {
    this.apiService = sps.getDataSourceApiService(BullhornNamespace.name);
    this.entityControllerService.entitySelected$.subscribe((entity) => {
      if (!entity) {
        return;
      }
      this.getProfile(entity).subscribe((profile) => {
        this.entityProfileSelectedSubject.next(profile);
      });
    });
    console.debug('BullhornEntityProfileService created');
  }

  getProfile(entity: IEntity): Observable<IEntityProfile> {
    return this.apiService.get(entity, { fields: this.getFields(entity) }).pipe(map((fatEntity) => this.transform(fatEntity)));
  }

  private getFields(entity: IEntity): string {
    switch (entity.type) {
      case BullhornEntityTypes.Candidate:
        return 'id,firstName,lastName,phone,email, companyName';
      case BullhornEntityTypes.ClientCorporation:
        return 'id,name,phone';
      case BullhornEntityTypes.ClientContact:
        return 'id,name,clientCorporation';
      case BullhornEntityTypes.Opportunity:
        return 'id,title,clientCorporation';
      case BullhornEntityTypes.JobOrder:
        return 'id,title';
      case BullhornEntityTypes.Placement:
        return 'id,candidate(firstName,lastName),jobOrder(title)';
      default:
        return 'id';
    }
  }

  private transform(entityData: any): IEntityProfile | undefined {
    switch (entityData.type) {
      case BullhornEntityTypes.Candidate:
        return {
          id: entityData.id,
          type: entityData.type,
          name: `${entityData.firstName} ${entityData.lastName}`,
          contactPhones: entityData.phone
            ? [
                {
                  displayTitle: 'Phone',
                  formattedPhone: entityData.phone,
                  fieldName: 'HomePhone',
                },
              ]
            : [],
          contactEmails: entityData.email
            ? [
                {
                  emailAddress: entityData.email,
                  isBusiness: false,
                  isPersonal: true,
                },
              ]
            : [],
          companyName: entityData.companyName,
        } as IEntityProfile;
      case BullhornEntityTypes.ClientCorporation:
        return {
          id: entityData.id,
          type: entityData.type,
          name: entityData.name,
          contactPhones: entityData.phone
            ? [
                {
                  displayTitle: 'Phone',
                  formattedPhone: entityData.phone,
                  fieldName: 'BusinessPhone',
                },
              ]
            : [],
          contactEmails: [],
        } as IEntityProfile;
      case BullhornEntityTypes.JobOrder:
        return {
          id: entityData.id,
          type: entityData.type,
          name: entityData.title,
          contactPhones: [],
          contactEmails: [],
        } as IEntityProfile;
      case BullhornEntityTypes.Placement:
        return {
          id: entityData.id,
          type: entityData.type,
          name: `${entityData.candidate.firstName} ${entityData.candidate.lastName} - ${entityData.jobOrder.title}`,
          contactPhones: [],
          contactEmails: [],
        } as IEntityProfile;
      case BullhornEntityTypes.ClientContact:
        return {
          id: entityData.id,
          type: entityData.type,
          name: entityData.name,
          companyName: entityData.clientCorporation.name,
        } as IEntityProfile;
      case BullhornEntityTypes.Opportunity:
        return {
          id: entityData.id,
          type: entityData.type,
          name: entityData.title,
          companyName: entityData.clientCorporation.name,
        } as IEntityProfile;
      default:
        throw new Error(`${entityData.type} not recognised generating profile.`);
    }
  }
}
